import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useAuth } from 'src/utils/auth';
import {
  Box,
  Heading,
  Text,
  Button,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Container,
  Flex,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  HStack,
  VStack,
  List,
  ListItem,
  ListIcon,
  UnorderedList,
} from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import { useCallback, useRef, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import useRequest from 'src/utils/hooks/useRequest';
import { useDeepEffect } from 'src/utils/hooks';
import CheckoutModal from 'src/components/molecules/checkout-modal';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { CONTAINER_MAX_WIDTH_1 } from 'src/constants/ui';

export const FREE_TIER_POSITION = 1;
const BASIC_TIER_POSITION = 2;
const PRO_TIER_POSITION = 3;

interface TierInfo {
  name: string;
  price: number;
  position: number;
  description: string;
  features: string[];
  details: React.ReactNode;
}

const TierOptionsPage = (): JSX.Element => {
  const { marketplace, urlBase } = useMarketplaceContext();
  const { hasAdmin } = useAuth();
  const isAdmin = hasAdmin(marketplace?.id);
  const router = useRouter();
  const currentTierPosition = marketplace?.tier?.position ?? 0;
  const toast = useToast();
  const { api } = useRequest();
  const [selectedTier, setSelectedTier] = useState<TierInfo | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [sessionKey, setSessionKey] = useState<number>(0);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const isMarketplaceTiersEnabled = useFeatureFlagEnabled('marketplace_tiers') ?? false;
  const { isOpen, onClose, onToggle } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Tier information with descriptions and key features
  const tierInfo = {
    free: {
      name: 'Free',
      price: 0,
      position: FREE_TIER_POSITION,
      description: 'Create a commerce platform for your audience',
      features: [
        'Customized UI for your audience',
        'Manually promote your platform to your audience or community',
        'No ability to collect user fees or commissions',
      ],
      details: (
        <UnorderedList className="list-disc list-inside" fontSize="sm" textAlign="left">
          <ListItem>Custom Filter Attributes</ListItem>
          <ListItem>Configurable UI</ListItem>
          <ListItem>Basic Emails to Users</ListItem>
          <ListItem>Single Category</ListItem>
          <ListItem>No User Payment (Free Classifieds or Free Directory)</ListItem>
          <ListItem>{`${marketplace?.slug}.withloma.com`}</ListItem>
        </UnorderedList>
      ),
    },
    basic: {
      name: 'Basic',
      price: 9,
      position: BASIC_TIER_POSITION,
      description: 'Generate some revenue and automate promotions',
      features: [
        'Built-in social marketing automation',
        'Use AI to launch with real listings on Day 1',
        'Charge fees for transactions, listings, or visibility',
      ],
      details: (
        <UnorderedList fontSize="sm" textAlign="left">
          <ListItem>Up to 3 Category</ListItem>
          <ListItem>Custom Filter Attributes</ListItem>
          <ListItem>Configurable UI</ListItem>
          <ListItem>Basic Emails to Users</ListItem>
          <ListItem>
            User Payment Enabled (Collect Fees from Marketplace, Classifieds, or Directory)
          </ListItem>
          <ListItem>{`${marketplace?.slug}.withloma.com`}</ListItem>
          <ListItem>
            Built-In Social Marketing Automation (Connect Directly to Your Social Accounts and
            Customize Promotions)
          </ListItem>
          <ListItem>1x AI-Powered Auto-Population</ListItem>
          <ListItem>One-at-a-Time Listing Import Tool</ListItem>
        </UnorderedList>
      ),
    },
    pro: {
      name: 'Pro',
      price: 49,
      position: PRO_TIER_POSITION,
      description: 'Turn your platform into a standalone business',
      features: [
        'Custom domain integration',
        'Unlimited AI-powered automation',
        'Advanced monetization options',
      ],
      details: (
        <UnorderedList fontSize="sm" textAlign="left">
          <ListItem>Unlimited Categories</ListItem>
          <ListItem>Custom Filter Attributes</ListItem>
          <ListItem>Configurable UI</ListItem>
          <ListItem>Advanced Custom Emails to Users</ListItem>
          <ListItem>
            User Payment Enabled (Collect Fees from Marketplace, Classifieds, or Directory)
          </ListItem>
          <ListItem>Custom Domain Integration</ListItem>
          <ListItem>
            Built-In Social Marketing Automation (Connect Directly to Your Social Accounts and
            Customize Promotions)
          </ListItem>
          <ListItem>Daily AI-Powered Auto-Population</ListItem>
          <ListItem>Bulk Listing Import Tool</ListItem>
          <ListItem>Connect Payouts to Your Stripe Account</ListItem>
        </UnorderedList>
      ),
    },
  };

  // Pricing card component for simplified design
  const PricingCard = ({ tier }: { tier: TierInfo }) => {
    const [showDetails, setShowDetails] = useState(false);
    const isPro = tier.position === PRO_TIER_POSITION;
    const isBasic = tier.position === BASIC_TIER_POSITION;
    const isFree = tier.position === FREE_TIER_POSITION;

    const getButtonText = () => {
      if (currentTierPosition > tier.position) return 'Downgrade';
      if (currentTierPosition === tier.position) return 'Current Tier';
      return 'Upgrade';
    };

    const getButtonProps = () => {
      if (currentTierPosition > tier.position) {
        return {
          colorScheme: 'primary' as const,
          onClick: () => changeTierByPosition(tier.position),
        };
      } else if (currentTierPosition === tier.position) {
        return {
          colorScheme: 'gray' as const,
          variant: 'outline' as const,
          isDisabled: true,
        };
      }
      return {
        colorScheme: 'primary' as const,
        onClick: () => changeTierByPosition(tier.position),
      };
    };

    const isCurrentTier = currentTierPosition === tier.position;
    const isUpgradeAvailable = currentTierPosition < tier.position;

    // Neutral visual differentiation (no loud colors)
    const cardBg = 'white';
    const borderColor = isCurrentTier ? 'primary.300' : 'gray.200';
    const borderWidth = isPro || isCurrentTier ? '2px' : isBasic ? '2px' : '1px';
    const shadow = isPro ? 'xl' : isBasic ? 'lg' : 'md';
    const topBarBg = isPro
      ? 'linear(to-r, gray.300, gray.200)'
      : isBasic
      ? 'linear(to-r, gray.200, gray.100)'
      : 'linear(to-r, gray.100, gray.100)';
    const headingColor = 'gray.800';
    const priceColor = 'gray.800';
    const iconColor = 'gray.500';

    return (
      <Card
        variant="outline"
        position="relative"
        bg={cardBg}
        borderColor={borderColor}
        borderWidth={borderWidth}
        shadow={shadow}
        h="full"
        transform={isPro ? 'scale(1.02)' : undefined}
        transition="all 0.2s ease"
        _hover={{ transform: isPro ? 'translateY(-3px) scale(1.025)' : 'translateY(-3px)' }}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="6px"
          bgGradient={topBarBg}
          borderTopLeftRadius="md"
          borderTopRightRadius="md"
        />
        {isCurrentTier && (
          <Box
            position="absolute"
            top="-10px"
            left="50%"
            transform="translateX(-50%)"
            bg="primary.500"
            color="white"
            px={3}
            py={1}
            borderRadius="md"
            fontSize="sm"
            fontWeight="bold"
          >
            Current Tier
          </Box>
        )}

        <CardHeader textAlign="center" pb={4}>
          <VStack spacing={4}>
            <Heading as="h3" size="lg" color={headingColor}>
              {tier.name}
            </Heading>

            <HStack justify="center" align="baseline">
              <Text fontSize="lg" color="gray.600">
                $
              </Text>
              <Text fontSize="4xl" fontWeight="bold" color={priceColor}>
                {tier.price}
              </Text>
              {tier.position !== FREE_TIER_POSITION && (
                <Text fontSize="lg" color="gray.600">
                  /mo
                </Text>
              )}
            </HStack>

            <Flex justify="center" align="center">
              <Text color="gray.600" textAlign="center" fontSize="md" px={2}>
                {tier.description}
              </Text>
            </Flex>
          </VStack>
        </CardHeader>

        <CardBody pt={0}>
          <VStack spacing={6} align="stretch">
            <List spacing={3}>
              {tier.features.map((feature, index) => (
                <ListItem key={index} display="flex" alignItems="flex-start">
                  <ListIcon as={FiCheck} color={iconColor} mt={0.5} />
                  <Text fontSize="sm" color="gray.800" fontWeight={isFree ? 'normal' : 'medium'}>
                    {feature}
                  </Text>
                </ListItem>
              ))}
            </List>

            <Flex justify="center" align="center">
              <Button
                variant="ghost"
                size="sm"
                color="gray.700"
                onClick={() => setShowDetails(!showDetails)}
                _hover={{ bg: 'gray.50' }}
                fontWeight="normal"
              >
                {showDetails ? 'Hide details' : 'Details...'}
              </Button>
            </Flex>

            {showDetails && <Box mt={2}>{tier.details}</Box>}

            {!selectedTier && (
              <Button
                size="lg"
                w="full"
                {...getButtonProps()}
                _hover={{
                  transform: 'translateY(-1px)',
                  shadow: 'md',
                }}
              >
                {getButtonText()}
              </Button>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Helper function to get tier info by position
  const getTierByPosition = (position: number): TierInfo => {
    const tierEntries = Object.entries(tierInfo);
    const tierEntry = tierEntries.find(([_, info]) => info.position === position);
    return tierEntry ? tierEntry[1] : tierInfo.free;
  };

  // Helper function to handle tier changes
  const changeTierByPosition = (position: number) => {
    const tier = getTierByPosition(position);
    changeTier(tier);
  };

  const tryCancelTier = async () => {
    setIsCancelling(true);
    try {
      await cancelTier();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel tier subscription. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const cancelTier = async () => {
    if (!marketplace) return;
    await api.marketplace.tier.cancel(marketplace.id);
    // clear the local cache of the marketplace
    const hostname = window.location.hostname || marketplace?.domain?.domain;
    localStorage.removeItem(`marketplace-${hostname}`);
    router.push('/admin/info?success=clear');
  };

  const changeTier = (tier: TierInfo) => {
    if (tier.position === FREE_TIER_POSITION) {
      // trigger downgrade directly with confirm dialog
      onToggle();
    } else {
      setSelectedTier(tier);
      setShowModal(true);
    }
  };

  const fetchClientSecret = useCallback(() => {
    if (urlBase === '' || marketplace === null || selectedTier === null) {
      return Promise.resolve('');
    }

    return api.marketplace.tier.createCheckoutSession(marketplace.id, {
      tier_position: selectedTier.position,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBase, marketplace?.id, selectedTier]);

  useDeepEffect(() => {
    const fetchCheckoutSession = async (marketplaceId: number, checkoutSessionId: string) => {
      const checkoutSession = await api.checkoutSession.get(marketplaceId, checkoutSessionId);
      if (checkoutSession.status === 'complete') {
        toast({
          title: 'Success',
          description:
            'Your payment was successfully processed! It may take up to a minute for the payment to process, try refreshing if the changes are not reflected immediately',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      }
    };

    if (router.isReady && router.query.checkout_session_id && marketplace?.id) {
      const checkoutSessionId = router.query.checkout_session_id as string;
      fetchCheckoutSession(marketplace.id, checkoutSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.checkout_session_id, marketplace?.id]);

  const shouldRenderPage = useMemo(() => {
    return marketplace && isAdmin && isMarketplaceTiersEnabled;
  }, [marketplace, isAdmin, isMarketplaceTiersEnabled]);

  // Early return if marketplace is not ready
  if (!marketplace) {
    return <div>Loading marketplace...</div>;
  }

  return (
    <Box as="section" w="full">
      <Box borderBottomWidth="1px" bg="bg.surface" w="full">
        <Container maxW={CONTAINER_MAX_WIDTH_1} py="10px">
          {shouldRenderPage ? (
            <>
              {/* Stripe checkout modal */}
              {selectedTier && (
                <CheckoutModal
                  key={sessionKey}
                  isOpen={showModal}
                  onClose={() => {
                    setShowModal(false);
                    setSessionKey(sessionKey + 1);
                    setSelectedTier(null);
                  }}
                  fetchClientSecret={fetchClientSecret}
                />
              )}

              <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      Revert to Free Tier
                    </AlertDialogHeader>
                    <AlertDialogBody>
                      Are you sure? Your tier subscription will remain active until the end of the
                      billing cycle.
                    </AlertDialogBody>

                    <AlertDialogFooter>
                      <Button ref={cancelRef} onClick={onClose} isDisabled={isCancelling}>
                        No, Go back
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={tryCancelTier}
                        ml={3}
                        isLoading={isCancelling}
                        loadingText="Cancelling..."
                      >
                        Yes, Revert to Free Tier
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>

              {/* Page title */}
              <VStack spacing={8} align="stretch">
                <Box textAlign="center" mb={8}>
                  <Heading as="h1" size="xl" mb={4}>
                    Choose Your Plan
                  </Heading>
                  <Text fontSize="lg" color="gray.600">
                    Select the perfect tier for your audience
                  </Text>
                </Box>

                {/* Pricing cards grid */}
                <SimpleGrid
                  columns={{ base: 1, lg: 3 }}
                  spacing={8}
                  maxW="1200px"
                  mx="auto"
                  w="full"
                >
                  <PricingCard tier={tierInfo.free} />
                  <PricingCard tier={tierInfo.basic} />
                  <PricingCard tier={tierInfo.pro} />
                </SimpleGrid>
              </VStack>
              <br />
              <br />
            </>
          ) : (
            <div>Marketplace tiers are not yet available.</div>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default TierOptionsPage;
